import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  signUp,
  confirmSignUp,
  signIn,
  signInWithRedirect,
  resendSignUpCode, // Import the resendSignUpCode method
} from 'aws-amplify/auth';
import { Form, Input, Button, Layout, Typography, Alert } from 'antd';
import { getUser, redirectIfLogin } from '../functions/userFuncs';
import axios from 'axios';

const { Content } = Layout;
const { Title, Text } = Typography;

const SignUpPage: React.FC<{ isLogged: boolean }> = ({ isLogged }) => {
  const [form] = Form.useForm();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isConfirmationStep, setIsConfirmationStep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false); // New state for resend button loading state
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    redirectIfLogin(navigate);
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
      // revisit this
    } catch (error) {
      setError('Error logging in with Google. Please try again.');
      console.error('Error logging in with Google:', error);
    }
  };

  const handleSignUp = async (values: any) => {
    try {
      const { email, password } = values;
      const response = await signUp({
        username: email, // Use email as the username
        password,
        options: {
          userAttributes: {
            email,
          },
          autoSignIn: true,
        },
      });

      if (response.nextStep) {
        const { nextStep } = response;
        if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          setIsConfirmationStep(true);
        } else if (nextStep.signUpStep === 'DONE') {
          navigate(`/login`, { state: { formPage: true } });
        }
      }
    } catch (error: any) {
      if (error.name === 'UsernameExistsException') {
        setError(
          'An account with this email already exists. Perhaps you meant to log in?'
        );
      } else {
        setError('Error signing up. Please try again.');
      }
      console.error('Error signing up:', error);
    }
  };

  const handleSignUpConfirmation = async (values: any) => {
    try {
      const { confirmationCode } = values;
      const response = await confirmSignUp({
        username: email, // Use email as the username for confirmation
        confirmationCode,
      });

      if (response.isSignUpComplete) {
        try {
          await signIn({ username: email, password });

          const formID = localStorage.getItem('form_id');

          const userData: any = {
            firstName,
            lastName,
            email,
          };

          if (formID) {
            userData.formID = formID;
            await axios.post(
              `${process.env.REACT_APP_INSTANCE}/api/user/register`,
              userData
            );
            localStorage.removeItem('form_id');
          }

          // Determine where to navigate after sign-up confirmation
          const user: any = await getUser();
          if (user)
            localStorage.setItem('User', JSON.stringify(user.attributes));
          const fromPage = location.state?.fromPage;
          const formState = location.state?.formState || {};

          if (location.state?.formPage) {
            navigate('/additional-info');
          } else {
            navigate('/status');
          }
        } catch (signInError: any) {
          console.error('Error signing in after confirmation:', signInError);
        }
      } else {
        setError('Sign up confirmation still requires further steps.');
      }
    } catch (error: any) {
      setError('Error confirming sign up. Please try again.');
      console.error('Error confirming sign up:', error);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    try {
      await resendSignUpCode({ username: email });
      setError('A new confirmation code has been sent to your email.');
    } catch (error: any) {
      setError('Error resending confirmation code. Please try again.');
      console.error('Error resending confirmation code:', error);
    } finally {
      setIsResending(false);
    }
  };

  const navigateToLogin = () => {
    if (location.state?.formPage)
      navigate('/login', { state: { formPage: true } });
    else navigate('/login');
  };

  return (
    <Layout className='login-page-background'>
      <Content className='login-layout-content'>
        <Title level={2}>
          {!isConfirmationStep ? 'Register' : 'Confirm Registration'}
        </Title>
        {isConfirmationStep && (
          <Text
            type='secondary'
            style={{ marginBottom: '20px', display: 'block' }}
          >
            Your confirmation code has been sent to your email address. Please
            check your junk mail folder.
          </Text>
        )}
        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            description={
              error.includes('already exists') ? (
                <Link to='/login'>Click here to log in.</Link>
              ) : null
            }
          />
        )}
        <Form
          form={form}
          layout='vertical'
          onFinish={
            !isConfirmationStep ? handleSignUp : handleSignUpConfirmation
          }
        >
          {!isConfirmationStep ? (
            <>
              <Form.Item name='firstName' label='First Name'>
                <Input
                  value={email}
                  required={true}
                  type='text'
                  name='firstName'
                  onChange={e => setFirstName(e.target.value)}
                />
              </Form.Item>
              <Form.Item name='lastName' label='Last Name'>
                <Input
                  value={email}
                  required={true}
                  type='text'
                  name='lastName'
                  onChange={e => setLastName(e.target.value)}
                />
              </Form.Item>

              <Form.Item
                name='email'
                label='Email'
                rules={[
                  {
                    required: true,
                    message: 'Please enter a valid email',
                    type: 'email',
                  },
                ]}
              >
                <Input value={email} onChange={e => setEmail(e.target.value)} />
              </Form.Item>
              <Form.Item
                name='password'
                label='Password'
                rules={[
                  { required: true, message: 'Please enter your password' },
                ]}
              >
                <Input.Password
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </Form.Item>
              <Form.Item
                name='confirmPassword'
                label='Confirm Password'
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          'The two passwords that you entered do not match!'
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name='confirmationCode'
                label='Confirmation Code'
                rules={[
                  {
                    required: true,
                    message: 'Please enter the confirmation code',
                  },
                ]}
              >
                <Input
                  value={confirmationCode}
                  onChange={e => setConfirmationCode(e.target.value)}
                />
              </Form.Item>
              <Button
                type='default'
                onClick={handleResendCode}
                loading={isResending}
                style={{ marginBottom: '20px' }}
              >
                Resend Code
              </Button>
            </>
          )}
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              {!isConfirmationStep ? 'Register' : 'Confirm Registration'}
            </Button>
          </Form.Item>
          <Button
            type='default'
            onClick={navigateToLogin}
            style={{ marginRight: '10px' }}
          >
            Log in
          </Button>
          <Button type='default' onClick={handleGoogleSignIn}>
            Sign In with Google
          </Button>
        </Form>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
            <img
              src='/Asset23@3x.png'
              alt='logo'
              style={{
                width: '150px',
              }}
            />
          </div>
      </Content>
    </Layout>
  );
};

export default SignUpPage;
